import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { BottomNavigation, BottomNavigationAction, Box, Paper } from "@mui/material";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import PageContent from "../page-content/PageContent";
import { SideNavContents } from "../sideNav";
import { ChatSurface } from "../ai-panel/ChatSurface";
import { SessionHistorySidebar } from "../ai-panel/SessionHistorySidebar";
import { CasLogo } from "../brand/CasLogo";
import { usePhoneView, PhoneView } from "../../context/phoneView";
import { useAiConnection } from "../../connection/AiConnectionProvider";
import { useOpenNewChat } from "../../hook/ai-panel";
import { useSelectedNotebookPageIdMap } from "../../hook/page";

// The Phone Shell (cas-mobile-shell D5; Terms: Phone Shell): below 600 px, one full-screen Phone View at a
// time between the app bar and a bottom bar. No drawers, so nothing opens over anything else.
const BAR_HEIGHT = 56;

export const PhoneShell: React.FC = () => {
  const { view, setView } = usePhoneView();
  const { activate } = useAiConnection();
  const newChat = useOpenNewChat();
  const { selectedNotebookPageId } = useSelectedNotebookPageIdMap();
  const [top, setTop] = useState(56);
  const firstPage = useRef(selectedNotebookPageId);

  // The app bar's real height (56 px, more with the editor toolbar), so no view hides under it.
  useLayoutEffect(() => {
    const bar = document.querySelector("header.MuiAppBar-root");
    if (!bar) return;
    const ro = new ResizeObserver(() => setTop(bar.getBoundingClientRect().height));
    ro.observe(bar);
    return () => ro.disconnect();
  }, []);

  // Chats and Cas need the socket, as opening the desktop panel or /chat does.
  useEffect(() => {
    if (view === "chats" || view === "cas") activate();
  }, [view, activate]);

  // Picking a page in Browse shows it.
  useEffect(() => {
    if (selectedNotebookPageId && selectedNotebookPageId !== firstPage.current) setView("page");
    firstPage.current = selectedNotebookPageId;
  }, [selectedNotebookPageId, setView]);

  const pane = (v: PhoneView) => ({
    display: view === v ? "flex" : "none",
    flexDirection: "column" as const,
    position: "fixed" as const,
    top: `${top}px`,
    bottom: `${BAR_HEIGHT}px`,
    left: 0,
    right: 0,
    overflow: v === "page" || v === "browse" ? "auto" : "hidden",
    overscrollBehavior: "contain",
  });

  return (
    <>
      <Box data-testid="phone-view-page" sx={{ ...pane("page"), p: 2 }}>
        <PageContent />
      </Box>
      <Box data-testid="phone-view-browse" sx={pane("browse")}>
        <SideNavContents />
      </Box>
      <Box data-testid="phone-view-chats" sx={pane("chats")}>
        <SessionHistorySidebar open fullWidth onNewChat={newChat} onPicked={() => setView("cas")} />
      </Box>
      <Box data-testid="phone-view-cas" sx={pane("cas")}>
        {view === "cas" && <ChatSurface historyRail={false} />}
      </Box>
      <Paper elevation={8} sx={{ position: "fixed", left: 0, right: 0, bottom: 0, zIndex: (t) => t.zIndex.appBar }}>
        <BottomNavigation
          data-testid="phone-nav"
          value={view}
          onChange={(_, v: PhoneView) => setView(v)}
          showLabels
          sx={{ height: BAR_HEIGHT }}
        >
          <BottomNavigationAction data-testid="phone-nav-page" value="page" label="Page" icon={<ArticleOutlinedIcon />} />
          <BottomNavigationAction data-testid="phone-nav-browse" value="browse" label="Browse" icon={<FolderOutlinedIcon />} />
          <BottomNavigationAction data-testid="phone-nav-chats" value="chats" label="Chats" icon={<ForumOutlinedIcon />} />
          <BottomNavigationAction data-testid="phone-nav-cas" value="cas" label="Cas" icon={<CasLogo size={22} />} />
        </BottomNavigation>
      </Paper>
    </>
  );
};
